import { useSymbolsMetadata } from "@shared/utils/hooks/misc.ts";
import { useExchangeTags } from "@shared/features/tradingTerminal/common/hooks/misc.ts";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  ExchangeTag,
  OnChangeWithWarning,
} from "@shared/features/tradingTerminal/common/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BotType, LanguageNameSpaces } from "@shared/types";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import { ErrorTypes } from "@shared/features/trades/common/types.ts";
import ExchangesSelector from "features/tradingTerminal/General/ExchangesSelector.tsx";
import { ErrorText } from "features/bots/common/styles.ts";
import Select from "common/Select/Select.tsx";
import { checkSymbolSearch } from "@shared/utils/misc.ts";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { isSymbol } from "@shared/types/utils.ts";
import {
  BotFormType,
  FormDataType,
} from "@shared/features/bots/BotForm/common/types.ts";
import { BotData } from "@shared/types";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants.ts";

interface useSignalSymbolAndExchangesSelectorsProps {
  onChangeWithWarning?: OnChangeWithWarning;
}

const useSignalSymbolAndExchangesSelectors = ({
  onChangeWithWarning,
}: useSignalSymbolAndExchangesSelectorsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const tradingTerminalData = useContext(TradingTerminalContext);
  const botFormData = useContext(BotFormContext);
  const { symbols, setSymbols, exchanges, setExchanges } =
    tradingTerminalData ?? botFormData ?? {};
  const { freeTextCreateTradeLoading, freeTextTradeData, generalErrors } =
    tradingTerminalData ?? {};
  const {
    botFormType,
    botGeneralData,
    initialized: formInitialized,
    addInitializedData,
    botType,
    useSymbolFromAlert,
    setUseSymbolFromAlert,
  } = botFormData ?? {};
  const isEdit = botFormType === BotFormType.EDIT;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const disableSymbols = isEdit || isBulkEdit;
  const isTvBots =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  const shouldShowAlertPlaceholder = isTvBots && useSymbolFromAlert;

  const [initialized, setInitialized] = useState(false);
  const { allSymbols } = useSymbolsMetadata();
  const parsedExchanges = isSymbol(exchanges) ? undefined : exchanges;
  const parsedSymbols = isSymbol(symbols) ? undefined : symbols;
  const symbol = parsedSymbols?.[0];
  const { getSymbolExchangesData, allExchanges } = useExchangeTags();
  const { exchangeToError, tagToValidExchanges } = useMemo(() => {
    const symbol =
      isSymbol(symbols) || useSymbolFromAlert
        ? undefined
        : symbols?.[0] ?? DEFAULT_SYMBOL;
    return getSymbolExchangesData(symbol);
  }, [symbol, getSymbolExchangesData, useSymbolFromAlert]);

  const getDefaultTagExchanges = (
    tagToValidExchanges: Record<string, string[]>
  ) => {
    const futuresExchanges = tagToValidExchanges[ExchangeTag.FUTURES];
    if (futuresExchanges.length) return futuresExchanges;
    return tagToValidExchanges[ExchangeTag.SPOT];
  };

  const defaultExchanges = getDefaultTagExchanges(tagToValidExchanges);

  useEffect(() => {
    if (initialized || isBulkEdit) return;
    let didInitialize = false;
    if (isEdit && formInitialized && botGeneralData) {
      const botData = botGeneralData as BotData | undefined;
      setSymbols?.(botData?.symbols || []);
      setExchanges?.(botData?.exchanges);
      didInitialize = true;
    } else if (
      !isEdit &&
      defaultExchanges.length &&
      !parsedExchanges?.length &&
      !parsedSymbols?.length
    ) {
      setSymbols?.([DEFAULT_SYMBOL]);
      setExchanges?.(defaultExchanges);
      didInitialize = true;
    }
    if (didInitialize) {
      addInitializedData?.(FormDataType.ACCOUNT_AND_SYMBOL);
      setInitialized(true);
    }
  }, [defaultExchanges, isEdit, formInitialized, botGeneralData]);

  useEffect(() => {
    if (symbol && !isBulkEdit) {
      setExchanges?.(
        parsedExchanges?.filter((exchange) => !(exchange in exchangeToError))
      );
    }
  }, [symbol]);

  useEffect(() => {
    if (!freeTextCreateTradeLoading && freeTextTradeData) {
      const freeTextSymbol = freeTextTradeData.symbol;
      setSymbols?.([freeTextSymbol]);
      const { tagToValidExchanges } = getSymbolExchangesData(
        freeTextTradeData.symbol
      );
      setExchanges?.(getDefaultTagExchanges(tagToValidExchanges));
    }
  }, [freeTextCreateTradeLoading, freeTextTradeData]);

  const symbolOptions = useMemo(() => {
    return allSymbols.map((symbol) => {
      return {
        label: (
          <TextWithIcon
            coinOrExchangeName={symbol}
            text={symbol}
            iconType={ICON_TYPES.COIN}
          />
        ),
        value: symbol,
      };
    });
  }, [allSymbols]);

  const getExchangesError = () => {
    if (botFormData) {
      return isSymbol(exchanges) || exchanges?.length
        ? undefined
        : t("messages.errors.select_at_least_one_exchange");
    }
    return generalErrors?.[ErrorTypes.SIGNAL_EXCHANGES];
  };

  const accountErrorText = getExchangesError();

  return {
    accountSelector: (
      <ExchangesSelector
        onChangeWithWarning={onChangeWithWarning}
        tagToValidExchanges={tagToValidExchanges}
        exchangeToError={exchangeToError}
        allExchanges={allExchanges}
      />
    ),
    accountError: accountErrorText ? (
      <ErrorText>{accountErrorText}</ErrorText>
    ) : undefined,
    accountSelectorTitle: t("labels.exchanges"),
    symbolSelector: (
      <Select
        placeholder={
          isSymbol(symbols)
            ? t("placeholders.multi")
            : shouldShowAlertPlaceholder
            ? TV_ALERT_SYMBOL
            : t("placeholders.choose_symbol")
        }
        onChange={(symbol: string) => {
          const setSymbolsFuc = () => {
            setSymbols?.([symbol]);
            setUseSymbolFromAlert?.(false);
          };
          if (onChangeWithWarning) {
            onChangeWithWarning(setSymbolsFuc).catch(() => {});
          } else {
            setSymbolsFuc();
          }
        }}
        value={shouldShowAlertPlaceholder ? TV_ALERT_SYMBOL : symbols?.[0]}
        optionLabelProp="label"
        style={{
          width: "100%",
        }}
        options={symbolOptions}
        showSearch={{
          filterOption: (searchValue, option) =>
            checkSymbolSearch(option?.value, searchValue),
        }}
        error={false}
        disabled={disableSymbols}
        mobileTitle={t("labels.symbol")}
      />
    ),
    symbolSelectorTitle: t("labels.symbol"),
    symbolError: undefined,
    noAccountModalOpen: undefined,
    closeNoAccountModal: undefined,
    accountIsValid: isSymbol(exchanges) || !!exchanges?.length,
    symbolIsValid: isSymbol(symbols) || !!useSymbolFromAlert || !!symbols?.[0],
    getExchangeErrorMessage: undefined,
  };
};

export default useSignalSymbolAndExchangesSelectors;

const DEFAULT_SYMBOL = "BTC/USDT";
